import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { contactInfo } from '../config/massSchedule';
import { apiService } from '../services/api';

const Footer: React.FC = () => {
  const [massSchedule, setMassSchedule] = useState<any[]>([]);

  useEffect(() => {
    loadMassTimes();
  }, []);

  const loadMassTimes = async () => {
    try {
      const data = await apiService.getMassTimes();
      setMassSchedule(data);
    } catch (error) {
      console.error('Error loading mass times for footer:', error);
      // Fallback to default
      setMassSchedule([
        { day: 'Sunday', times: '8:00 AM, 10:30 AM, 6:00 PM' },
        { day: 'Weekdays', times: '7:00 AM, 12:10 PM' },
        { day: 'Saturday', times: '5:30 PM (Vigil)' },
        { day: 'Holy Days', times: '7:00 AM, 12:10 PM, 7:00 PM' }
      ]);
    }
  };

  return (
    <footer className="bg-church-dark text-white" role="contentinfo">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div>
            <h3 className="text-xl font-serif font-semibold mb-4 text-church-gold">Contact Us</h3>
            <address className="not-italic">
              <p className="mb-2">{contactInfo.churchName}</p>
              <p className="mb-2">{contactInfo.address}</p>
              <p className="mb-2">{contactInfo.diocese}</p>
              <p className="mb-2">
                <a href={`tel:+44${contactInfo.phone.replace(/\s/g, '')}`} className="hover:text-church-gold transition-colors">
                  {contactInfo.phone}
                </a>
              </p>
              <p className="mb-2">
                <a href={`mailto:${contactInfo.email}`} className="hover:text-church-gold transition-colors">
                  {contactInfo.email}
                </a>
              </p>
            </address>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-serif font-semibold mb-4 text-church-gold">Quick Links</h3>
            <nav aria-label="Footer navigation">
              <ul className="space-y-2">
                <li>
                  <Link to="/calendar" className="hover:text-church-gold transition-colors">
                    Event Calendar
                  </Link>
                </li>
                <li>
                  <Link to="/reflections" className="hover:text-church-gold transition-colors">
                    Priest's Reflections
                  </Link>
                </li>
                <li>
                  <Link to="/history" className="hover:text-church-gold transition-colors">
                    Our History
                  </Link>
                </li>
                <li>
                  <Link to="/team" className="hover:text-church-gold transition-colors">
                    Parish Team
                  </Link>
                </li>
                <li>
                  <Link to="/memorial" className="hover:text-church-gold transition-colors">
                    In Memoriam
                  </Link>
                </li>
                <li>
                  <button 
                    onClick={() => window.open('https://donate.mydona.com/all-saints-thirsk', '_blank')}
                    className="hover:text-church-gold transition-colors focus:outline-none focus:ring-2 focus:ring-church-gold rounded text-left"
                  >
                    Online Giving
                  </button>
                </li>
              </ul>
            </nav>
            
            {/* Social Media */}
            <div className="mt-6">
              <h4 className="text-lg font-semibold mb-3 text-church-gold">Follow Us</h4>
              <div className="flex space-x-4">
                <a
                  href="https://www.facebook.com/AllSaintsThirsk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-church-gold transition-colors"
                  aria-label="Visit our Facebook page"
                >
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a
                  href="https://www.youtube.com/@AllSaintsChurchThirsk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-church-gold transition-colors"
                  aria-label="Visit our YouTube channel"
                >
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Mass Times */}
          <div>
            <h3 className="text-xl font-serif font-semibold mb-4 text-church-gold">Mass Times</h3>
            <div className="space-y-2">
              {massSchedule.map((schedule, index) => (
                <p key={index}>
                  <strong>{schedule.day}:</strong> {schedule.times}
                </p>
              ))}
            </div>
          </div>
        </div>

        <hr className="border-gray-600 my-8" />

        <div className="flex flex-col md:flex-row justify-between items-center">
          <p className="text-sm text-gray-300">
            © 2025 Anselm Tulloch. All Rights Reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;